import {
    FLEX_CREATE_TOKEN_ERRORS_ENUM,
    FLEX_FIELDS,
    FLOW_STEP_BILLING,
    MAPPED_FLEX_TOKEN_ERRORS_ENUM,
    PATH_ORDER_CONFIRMATION,
    PATH_PENDING,
    PATH_PRODUCT_LOGIN,
    SELECTED_NUMBER_TYPE,
    UAP_LOGIN_ENABLED,
    PATH_SIGNUP_SUCCESS
} from '~/common/utils/consts.js';
import { FLEX_EXPIRATION_DATE_ERROR, FLEX_EXPIRED_FORM, FLEX_GENERIC_ERROR, FLEX_INCOMPLETE_DATA } from '~/common/utils/errorMessaging';
// Import Dependencies
import React, { Component, Fragment } from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import { createBillingPageDataEvent, createPersonalizedPurchaseEvent, createSignupErrorEvent, gtm, scBeacon, loadFormEvent, startFormEvent, submitFormEvent, abandonFormEvent, startBuyFlowEvent, completeBuyFlowEvent, errorFormEvent, purchaseEvent, viewContentEvent, errorPurchaseEvent } from '~/common/utils/tracking.js';
import { inject, observer } from 'mobx-react';
import { reaction } from 'mobx';
import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';

import AccountInfo from '~/views/Billing/components/AccountInfo';
import ApiError from '~/common/components/ApiError';
import BackButton from '~/common/components/BackButton';
import BillingInfo from '~/views/Billing/components/BillingInfo';
import FingerPrint from '~/common/components/FingerPrint';
import Flexframe from '~/common/components/FlexFrame';
// Import Components
import HeaderGroup from '~/common/components/HeaderGroup';
import NewsletterCheckbox from '~/common/components/NewsletterCheckbox';
import OrderSummary from '~/views/Billing/components/OrderSummary';
import BillingAddress from '~/views/Billing/components/BillingAddress';
import Stepper from '~/common/components/Stepper';
import Button from '~/common/components/CommonButton';
import each from 'lodash/each';
import fatalErrorModal from '~/common/utils/fatalErrorModal.js';
import isPlainObject from 'lodash/isPlainObject';
import uniq from 'lodash/uniq';
import { setSessionStorage } from '~/common/utils/storage';
// import { qualtricsBannerInit, qualtricsBannerObserver } from '~/common/utils/qualtricsBanner';
import { pathProductLogin } from '~/common/globals';
import styles from './billing.css';

@withRouter
@inject(
    'pendingStore',
    'offerSuiteStore',
    'lockNumberStore',
    'transferNumberStore',
    'promoCodeStore',
    'convertToPaidStore',
    'formsStore',
    'sharedFormUtilsStore',
    'flexFrameStore',
    'userLocationStore',
    'requestLocalNumberStore',
    'numberLockSessionStore',
    'billingInfoStore',
    'directPurchaseStore',
    'modalStore',
    'emailCaptureStore',
    'accountInfoStore',
    'loginStore',
    'productTrackingStore'
)
@observer
export default class Billing extends Component {
    state = {
        createButtonText: 'Create my account',
        lmiUAPLoginEnabled: false,
        formSuccessSubmission: false
    };

    componentDidMount() {
        const { promoCodeStore, convertToPaidStore, formsStore, flexFrameStore, loginStore, lockNumberStore } = this.props;
        const { isUserConvertingToPaid } = convertToPaidStore;
        const { authToken, userInfo } = loginStore;

        gtm(viewContentEvent('buy flow', 'checkout', SELECTED_NUMBER_TYPE[lockNumberStore.selectedNumberType]));

        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        // stop user from interacting with the form until the microform is created;
        flexFrameStore.setCreatingMicroform(true);

        // if converting to paid and we have the userInfo
        if (authToken && isUserConvertingToPaid && userInfo) {
            const fields = [
                'FirstName',
                'LastName',
                'Email'
            ];
            // populate the user information
            fields.forEach((field) => {
                if (userInfo[field]) {
                    formsStore.setFormData(field, userInfo[field]);
                } else {
                    throw new Error('Missing necessary information');
                }
            });
        }

        // Set form type
        formsStore.setFormType('buy-form');

        // track billing page's initial load
        gtm(createBillingPageDataEvent());

        // Track Buy Flow start
        gtm(startBuyFlowEvent());

        // Track buy form load
        gtm(loadFormEvent('buy-form'));

        // if promo code is valid open banner again
        promoCodeStore.setIsBannerOpen(promoCodeStore.isCouponValid);

        this.observeUAPLogin();
    }

    componentWillUnmount() {
        if (this.props.formsStore.IsFormInteracted && !this.state.formSuccessSubmission) {
            gtm(abandonFormEvent('buy-form'));
        }
        // tear down the microform
        this.props.flexFrameStore.destroyFlexFrame();
    }

    observeUAPLogin = () => {
        if (UAP_LOGIN_ENABLED) {
            this.setState({
                lmiUAPLoginEnabled: UAP_LOGIN_ENABLED
            });
        }
    };

    refreshBillingInfo() {
        const { flexFrameStore, formsStore } = this.props;
        flexFrameStore.refreshMicroform();
        // empty security code and exp date
        formsStore.setFormData('ExpirationDate', '');
        formsStore.setFieldError('ExpirationDate', ' ');
    }

    flexFrameErrorHandling(err) {
        const { flexFrameStore, formsStore } = this.props;
        if (!err?.reason) {
            this.handleFlexGenericError(err);
            return;
        }

        const foundReason = this.mapFlexResponseErrorType(err);
        switch (foundReason) {
            case MAPPED_FLEX_TOKEN_ERRORS_ENUM.INVALID_TOKEN:
                gtm(errorPurchaseEvent(FLEX_EXPIRED_FORM));
                flexFrameStore.setFlexFrameError(FLEX_EXPIRED_FORM);
                // empty billing fields
                this.refreshBillingInfo();
                break;
            case MAPPED_FLEX_TOKEN_ERRORS_ENUM.EXP_DATE_ERROR:
                gtm(errorFormEvent('buy-form', 'ExpirationDate', FLEX_EXPIRATION_DATE_ERROR));
                formsStore.setFieldError('ExpirationDate', FLEX_EXPIRATION_DATE_ERROR);
                break;
            case MAPPED_FLEX_TOKEN_ERRORS_ENUM.FIELDS_ERROR: {
                const fieldsWithError = err?.details ? this.determineFlexResponseErrorFields(err.details) : FLEX_FIELDS;
                flexFrameStore.turnOnFieldsError(fieldsWithError, true);
                break;
            }
            case MAPPED_FLEX_TOKEN_ERRORS_ENUM.NO_VALUES_ERROR:
                gtm(errorPurchaseEvent(FLEX_INCOMPLETE_DATA));
                flexFrameStore.setFlexFrameError(FLEX_INCOMPLETE_DATA);
                flexFrameStore.turnOnFieldsError();
                break;
            default:
                // catch all error
                this.handleFlexGenericError(err);
                break;
        }
    }

    mapFlexResponseErrorType(err) {
        let foundReason = '';
        const errorHasDetails = err?.details?.length !== undefined && err.details.length > 0;

        // Check the possible reasons for invalid token
        if (err.reason === FLEX_CREATE_TOKEN_ERRORS_ENUM.TIMEOUT ||
            err.reason === FLEX_CREATE_TOKEN_ERRORS_ENUM.VALIDATION_ERROR) {
            // No specific field error means token error
            if (!errorHasDetails) {
                foundReason = MAPPED_FLEX_TOKEN_ERRORS_ENUM.INVALID_TOKEN;
            }
        }

        // Check the possible reasons for invalid field values
        if (err.reason === FLEX_CREATE_TOKEN_ERRORS_ENUM.DATA_ERROR ||
            err.reason === FLEX_CREATE_TOKEN_ERRORS_ENUM.VALIDATION_ERROR) {
            if (errorHasDetails) {
                foundReason = MAPPED_FLEX_TOKEN_ERRORS_ENUM.FIELDS_ERROR;
            }
        }

        // Check the possible reasons for non available field values
        if (err.reason === FLEX_CREATE_TOKEN_ERRORS_ENUM.NO_DATA_AVAILABLE ||
            err.reason === FLEX_CREATE_TOKEN_ERRORS_ENUM.NO_DATA_FIELDS_AVAILABLE) {
            foundReason = MAPPED_FLEX_TOKEN_ERRORS_ENUM.NO_VALUES_ERROR;
        }

        // Check the possible reasons for invalid expiration date error
        if (err.reason === FLEX_CREATE_TOKEN_ERRORS_ENUM.INVALID_PARAMS) {
            foundReason = MAPPED_FLEX_TOKEN_ERRORS_ENUM.EXP_DATE_ERROR;
        }

        return foundReason;
    }

    determineFlexResponseErrorFields(errorDetails) {
        const errorFields = [];
        if (Array.isArray(errorDetails)) {
            errorDetails.forEach((errObj) => {
                // check if the error was related to one of the fields
                if (errObj?.location && FLEX_FIELDS.includes(errObj.location)) {
                    errorFields.push(errObj.location);
                }
            });
        }
        return errorFields;
    }

    handleFlexGenericError(err) {
        const { flexFrameStore } = this.props;

        flexFrameStore.setFlexFrameError(FLEX_GENERIC_ERROR);
        gtm(errorPurchaseEvent(FLEX_GENERIC_ERROR));

        // track error in Sentry
        Sentry.captureMessage('flexFrameError', { extra: {
            error: 'Token Error',
            errorMessage: err
        } });
    }

    signupErrorHandling(err) {
        const { formsStore, directPurchaseStore, pendingStore, flexFrameStore } = this.props;
        // error data object
        // TODO: institute deepGet here for safe error handling
        const errorData = err.response && err.response.data ? err.response.data : err;
        // if model error (incorrect input fields)
        if (typeof errorData === 'object' && errorData.ErrorType === 'ModelValidationError') {
            const modelValidationErrors = [];
            // create an array of 2004 and 2003 form errors or if email is invalid
            each(errorData.Details, (value) => {
                if (value.ErrorCode === 2004 || value.ErrorCode === 2003 || (value.ErrorCode === 0 && value.Field === 'signupData.CustomerInfo.Email')) {
                    modelValidationErrors.push(value.Field.split('.').pop());
                }
            });
            // set billing form field errors
            each(uniq(modelValidationErrors), (value) => {
                // if there is no offer or number selected
                if (value === 'OfferId' || value === 'SubscriptionNumber') {
                    gtm(errorPurchaseEvent("user can't sign up"));
                    // user can't sign up
                    return fatalErrorModal();
                } else if (value === 'ExpirationYear' || value === 'ExpirationMonth') {
                    gtm(errorFormEvent('buy-form', 'ExpirationDate', 'expire date error'));
                    formsStore.setFieldError('ExpirationDate', ' ');
                } else {
                    gtm(errorFormEvent('buy-form', value, 'error'));
                    formsStore.setFieldError(value, ' ');
                }

                return true;
            });

            gtm(errorPurchaseEvent('Please correct any errors below and try submitting your information again.'));
            // set message in billing error box
            directPurchaseStore.setSignupError('Please correct any errors below and try submitting your information again.');
            // close modal on error
            return directPurchaseStore.setCreatingAccount(false);
        }
        // Fatal core errors, or logic problem with inputs
        if (
            typeof errorData === 'object' &&
            (errorData.ErrorType === 'CoreError' || errorData.ErrorType === 'LogicalError')
        ) {
            // specific error codes
            if (
                errorData.Details[0] &&
                typeof errorData.Details[0].ErrorCode === 'number'
            ) {
                // store error code to check against
                const errorCode = errorData.Details[0].ErrorCode;
                // if number is no longer in session
                if (errorCode === 5006 || errorCode === 5007) {
                    // open up number no longer in session modal
                    this.props.modalStore.setModalOptions({
                        isModalOpen: true,
                        isModalClosable: false,
                        modalType: 'medium',
                        childComponentString: 'cantLockNumberToSessionModalContent'
                    });
                    gtm(errorPurchaseEvent("can't lock number to session"));
                    return directPurchaseStore.setCreatingAccount(false);
                }
                // invalid credit card expiration
                if (errorCode === 3003) {
                    // set field data to invalid
                    formsStore.setFieldError('ExpirationDate', 'Please enter a valid expiration date.');
                    gtm(errorFormEvent('buy-form', 'ExpirationDate', 'Please enter a valid expiration date.'));
                    // close modal on error
                    return directPurchaseStore.setCreatingAccount(false);
                }
                // credit card, security code, and pending signup errors
                if (errorCode === 5005) {
                    // pending signup
                    if (
                        typeof errorData.Details[0].Cause === 'string' &&
                        errorData.Details[0].Cause === 'Cannot perform Signup' &&
                        typeof errorData.Details[0].AdditionalMessage === 'string' &&
                        errorData.Details[0].AdditionalMessage.indexOf('reference order number') >= 0
                    ) {
                        // pass reference number string
                        pendingStore.setReferenceNumber(errorData.Details[0].AdditionalMessage);
                        // pass selected number
                        pendingStore.setSelectedNumber(this.props.lockNumberStore.selectedNumberUI);
                        gtm(errorPurchaseEvent(errorData.Details[0].AdditionalMessage));

                        // user will be redirected to pending page with these two values
                        // close modal on error
                        return directPurchaseStore.setCreatingAccount(false);
                    }
                    // if the API rejects the email used
                    if (
                        typeof errorData.Details[0].AdditionalMessage === 'string' &&
                        errorData.Details[0].AdditionalMessage === 'The email address you entered could not be verified. Please check the address and try again.'
                    ) {
                        // set field data to invalid
                        formsStore.setFieldError('Email', 'The email address you entered could not be verified. Please check the address and try again.');
                        gtm(errorFormEvent('buy-form', 'Email', 'The email address you entered could not be verified. Please check the address and try again.'));

                        // close modal on error
                        return directPurchaseStore.setCreatingAccount(false);
                    }
                    // invalid credit card number
                    if (
                        typeof errorData.Details[0].AdditionalMessage === 'string' &&
                        (errorData.Details[0].AdditionalMessage === 'Invalid credit card number. Please re-check the credit card number you entered.' || errorData.Details[0].AdditionalMessage === 'The credit card number you entered could not be verified. Please check the credit card number and try again.')
                    ) {
                        gtm(errorFormEvent('buy-form', 'number', errorData.Details[0].AdditionalMessage));
                        // set field data to invalid
                        flexFrameStore.setIsCardValid(false);
                        // set message in billing error box
                        directPurchaseStore.setSignupError(errorData.Details[0].Cause);
                        // close modal on error
                        return directPurchaseStore.setCreatingAccount(false);
                    }
                }
                // invalid zip code
                if (errorCode === 5003 || errorCode === 3005 || errorCode === 3006) {
                    // set field data to invalid
                    formsStore.setFieldError('ZipCode', 'Please enter a valid postal code.');
                    gtm(errorFormEvent('buy-form', 'ZipCode', 'Please enter a valid postal code.'));

                    // close modal on error
                    return directPurchaseStore.setCreatingAccount(false);
                }
                // if card is a pre paid card
                if (errorCode === 5008) {
                    // track error
                    gtm(createSignupErrorEvent('buy-form', 'number', errorData.Details[0].Message));
                    // set field data to invalid
                    flexFrameStore.setIsCardValid(false);
                    gtm(errorFormEvent('buy-form', 'number', errorData.Details[0].AdditionalMessage));
                    // set API error
                    directPurchaseStore.setSignupError(errorData.Details[0].Message);
                    // close modal on error
                    return directPurchaseStore.setCreatingAccount(false);
                }
                // if transaction declined by desicion manager
                if (errorCode === 5009) {
                    gtm(createSignupErrorEvent('buy-form', 'form', 'Something went wrong. Please try again.'));
                    gtm(errorPurchaseEvent('Something went wrong. Please try again.'));

                    directPurchaseStore.setSignupError('Something went wrong. Please try again.');
                    return directPurchaseStore.setCreatingAccount(false);
                }
                // authorization failure errors
                if (errorCode === 5010) {
                    gtm(createSignupErrorEvent('buy-form', 'form', 'Please correct any errors below and try submitting your information again.'));
                    // set error message
                    directPurchaseStore.setSignupError('Please correct any errors below and try submitting your information again.');
                    gtm(errorPurchaseEvent('Please correct any errors below and try submitting your information again.'));

                    // refresh microform
                    this.refreshBillingInfo();
                    return directPurchaseStore.setCreatingAccount(false);
                }
                // subscription number not tied to session or general core error then start over
                if (errorCode === 5999) {
                    gtm(createSignupErrorEvent('buy-form', 'form', 'subscription number not tied to session'));
                    gtm(errorPurchaseEvent('subscription number not tied to session'));

                    directPurchaseStore.setCreatingAccount(false);
                    return fatalErrorModal();
                }
            }
            gtm(createSignupErrorEvent('buy-form', 'form', 'Something went wrong. Please try again.'));
            gtm(errorPurchaseEvent('Something went wrong. Please try again.'));

            // CoreError or LogicalError, but we don't know what happened, user can try again
            // set message in billing error box
            directPurchaseStore.setSignupError('Something went wrong. Please try again.');
            // post error to Sentry
            Sentry.captureMessage('signupError', { extra: {
                errorCode: errorData.Details[0].ErrorCode,
                errorMessage: errorData.Details[0].AdditionalMessage
            } });
            // close modal on error
            return directPurchaseStore.setCreatingAccount(false);
        }
        directPurchaseStore.setCreatingAccount(false);
        // otherwise we don't know what happened, start over
        fatalErrorModal();
        return err;
    }

    signupObject() {
        const {
            FirstName,
            LastName,
            ContactPhone,
            Email,
            UserKey,
            Password,
            Username,
            NameOnCard,
            ExpirationMonth,
            ExpirationYear,
            Street1,
            City,
            ZipCode,
            StateCode,
            CountryCode,
            IsNewsletter
        } = this.props.formsStore;
        const { userLocationStore, flexFrameStore, offerSuiteStore, convertToPaidStore, lockNumberStore, billingInfoStore, requestLocalNumberStore, promoCodeStore, transferNumberStore, productTrackingStore } = this.props;
        const { selectedState, requestConfirmed, requestedAreaCode, requestedCity } = requestLocalNumberStore;
        const { countryHasStates } = billingInfoStore;
        const { deviceType, deviceID, isRubySignup } = productTrackingStore;

        const signupObj = {
            CustomerInfo: {
                FirstName,
                LastName,
                ContactPhone: ContactPhone.replace(/\D/g, ''),
                Email,
                CreditCardInfo: {
                    NameOnCard,
                    MaskedNumber: flexFrameStore.maskCard,
                    CreditCardType: flexFrameStore.finalCardType,
                    SecurityCode: null,
                    ExpirationMonth,
                    ExpirationYear
                },
                BillingAddress: {
                    Street1,
                    City,
                    ZipCode,
                    StateCode: countryHasStates ? StateCode : 'NA',
                    CountryCode
                }
            },
            ClientIpAddress: userLocationStore.ipAddress,
            IsTransferNeeded: convertToPaidStore.isUserConvertingToPaid ? convertToPaidStore.isTransferNeededBool : transferNumberStore.transferNumber,
            PromoCode: promoCodeStore.promoCodeName || null,
            PromoId: promoCodeStore.promoCodeId || null,
            OfferId: offerSuiteStore.selectedOfferId,
            OfferSuiteId: offerSuiteStore.offerSuiteId,
            PartnerId: null,
            TokenJson: flexFrameStore.tokenString,
            PublicKey: null,
            DeviceFingerprintID: flexFrameStore.FingerPrintID,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            browserLanguage: navigator.language || navigator.userLanguage,
            browserUserAgent: navigator.userAgent,
            browserAcceptHeader: 'application/json, text/plain, */*'
        };

        if (UserKey) {
            // if user is signed into LMI
            signupObj.LmiUserKey = UserKey;
        }

        if (Password) {
            // if user is not signed into LMI
            signupObj.CustomerInfo.Password = Password;
        }

        if (!convertToPaidStore.isUserConvertingToPaid) {
            // signup object for direct buy flow
            signupObj.CustomerInfo.Username = Username || undefined;
            signupObj.SubscriptionNumber = lockNumberStore.selectedNumberE164;
            signupObj.RequestedStateCode = selectedState || null;
            signupObj.RequestedLocalNumber = requestConfirmed ? `${requestedAreaCode}, ${requestedCity}` : null;
            // all plans include this
            signupObj.OptInSms = true;
            signupObj.OptInReadYourVoicemail = true;
            signupObj.IsNewsletter = IsNewsletter;
        }

        // FIXME: remove afer pricing test is over PLANS C
        if (offerSuiteStore.usersPerAccount) signupObj.UserCount = offerSuiteStore.usersPerAccount;

        // add product tracking params if they are present
        if (deviceType !== '') signupObj.deviceType = deviceType;
        if (deviceID !== '') signupObj.deviceID = deviceID;
        if (isRubySignup) signupObj.IsRubyAffiliateSignUp = isRubySignup;

        return signupObj;
    }

    // redirect to either password reset or login page
    redirectConfirmation(redirectUrl) {
        setTimeout(() => {
            const { directPurchaseStore } = this.props;
            // If qualtrics banner shows up
            if (directPurchaseStore.qualtricsBannerShowing) {
                // redirect after 2 secs
                setTimeout(() => {
                    window.onbeforeunload = null;
                    window.location = redirectUrl;
                }, 2000);
            } else {
                window.onbeforeunload = null;
                window.location = redirectUrl;
            }
        }, 4000);
    }

    submitOrder() {
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        const { isUserConvertingToPaid } = this.props.convertToPaidStore;
        const { emailAlreadyUsedError } = this.props.sharedFormUtilsStore;
        const { flexFrameStore, billingInfoStore, convertToPaidStore, formsStore, emailCaptureStore, directPurchaseStore, history, productTrackingStore, offerSuiteStore, loginStore } = this.props;
        const { authToken } = loginStore;
        const fields = [
            'FirstName',
            'LastName',
            'Email',
            'NameOnCard',
            'ContactPhone',
            'Street1',
            'City',
            'ZipCode',
            'CountryCode',
            'ExpirationDate'
        ];

        // if user is not logged in, push the 'password' field to validate
        if (!authToken) fields.push('Password');

        // if user name is needed
        if (!isUserConvertingToPaid && emailAlreadyUsedError) fields.push('Username');

        // if the country has states add the field to the array
        if (billingInfoStore.countryHasStates) fields.push('StateCode');
        // check before running any validation for errors from the API
        if (formsStore.doesFormHaveErrors(['Email'])) {
            gtm(errorFormEvent('buy-form', 'Email', 'invalid'));
            return false;
        }

        // validate the form
        formsStore.validateForm(fields, true);

        // if credit card or verification code is invalid display error message
        if (!flexFrameStore.isCardValid || !flexFrameStore.isCodeValid) {
            if (!flexFrameStore.isCardValid) {
                gtm(errorFormEvent('buy-form', 'number', 'invalid number'));
                flexFrameStore.setUserInteractedWithCardField(true);
            }
            if (!flexFrameStore.isCodeValid) {
                gtm(errorFormEvent('buy-form', 'securityCode', 'invalid security code'));
                flexFrameStore.setUserInteractedWithCodeField(true);
            }
            return false;
        }

        // if no errors
        if (formsStore.doesFormHaveErrors(fields)) return false;

        // create flexframe token
        flexFrameStore.createToken({
            // Set card type to send the right one when selected by user
            type: flexFrameStore.cardType,
            expirationMonth: formsStore.ExpirationMonth,
            expirationYear: formsStore.ExpirationYear
        }, () => {
            // if success
            flexFrameStore.setFlexFrameError('');
            const signupObj = this.signupObject();

            // set Qualtrics
            const { calculatedTotal, orderTotal } = isUserConvertingToPaid ? this.props.convertToPaidStore : this.props.billingInfoStore;
            const { offerRecurringCharge, smsPerNumberCharge, isTermAnnual, selectedPlanTerm } = this.props.offerSuiteStore;

            // if sms enabled and requested then sms fee, or 0
            const smsCharge = smsPerNumberCharge || 0;
            // if term us annual, multiply it by 12
            const preCalculatedCost = isTermAnnual ? (offerRecurringCharge + smsCharge) * 12 : (offerRecurringCharge + smsCharge);
            const total = calculatedTotal ? orderTotal[selectedPlanTerm].TotalAmount : preCalculatedCost;

            setSessionStorage('qualtrics_data_gh', {
                unique_identifier: Cookies.get('_ga'),
                email: signupObj.CustomerInfo.Email,
                sku: signupObj.OfferId,
                purchase_term: offerSuiteStore.isTermMonthly ? 'monthly' : 'annual',
                order_total: total,
                promo_code: signupObj.PromoCode || ''
            });

            if (isUserConvertingToPaid) {
                // convert to pay flow
                convertToPaidStore.convertFreeTrial(
                    signupObj,
                    (response) => {
                        // if success
                        const { data } = response;
                        if (typeof data.Number === 'string' && typeof data.Username === 'string' && isPlainObject(data.Charges)) {
                            // ToDo :: Old track conversion. Will remove once confirmed by GA after new updates
                            // gtm(createConversionEvent(data));
                            // track SCBeacon: successful trial to paid signup
                            scBeacon(createPersonalizedPurchaseEvent('2'));
                            this.setState({ formSuccessSubmission: true });
                            gtm(submitFormEvent('buy-form'));
                            gtm(purchaseEvent(data));
                            gtm(completeBuyFlowEvent(data));
                            convertToPaidStore.setTrialConverted(true);
                            // send user to ruby upsell page
                            history.push(PATH_ORDER_CONFIRMATION);
                            // if no response body
                        } else if (!data) {
                            const message = 'no response body';
                            // track error in Sentry
                            Sentry.captureMessage('convertToPaidError', {
                                extra: {
                                    errorMessage: message
                                }
                            });
                            // return error
                            throw new Error(message);
                            // response success is false
                        } else if (data.Success === false) {
                            const billingFields = ['Street1', 'City', 'ZipCode', 'StateCode', 'CountryCode'];
                            const userInfoFields = ['ContactPhone', 'NameOnCard', 'ExpirationDate', ...billingFields];

                            if (!data.ErrorText) {
                                gtm(errorPurchaseEvent('your session has expired please log in again'));
                                // add error session expired copy to the modal
                                convertToPaidStore.setLoginError('your session has expired please log in again');
                                // clear user info
                                convertToPaidStore.setAuthToken('');

                                convertToPaidStore.setTimeoutErrorStep('signup');
                            }

                            switch (data.ErrorText) {
                                case 'AccessPoint in Use by Another Customer': // number is no longer reserved for this account
                                // break omitted
                                case 'Invalid Account Status': // account is suspended for some reason
                                // break omitted
                                case 'Invalid numberLockSessionId': // session Id Expired
                                    gtm(errorPurchaseEvent(data.ErrorText));
                                    fatalErrorModal();
                                    break;
                                // the is no trial account
                                case 'Not Free Trial Account':
                                    gtm(errorPurchaseEvent('Our records indicate you are already a paid customer. Please log into your account to make any changes to your billing information.'));
                                    convertToPaidStore.setSignupError('Our records indicate you are already a paid customer. Please log into your account to make any changes to your billing information.');
                                    convertToPaidStore.setCanTrialBeConverted(false);
                                    break;
                                // issue with customer info
                                case 'Invalid Customer Info':
                                    userInfoFields.forEach((field) => {
                                        formsStore.setFieldError(field, ' ');
                                    });
                                    convertToPaidStore.setSignupError('Please correct any errors below and try submitting your information again.');
                                    break;
                                // invalid address
                                case 'Invalid Contact Address':
                                // break omitted
                                case 'Invalid State Code':
                                // break omitted
                                case 'Invalid ZIP Code':
                                    billingFields.forEach((field) => {
                                        formsStore.setFieldError(field, ' ');
                                    });
                                    convertToPaidStore.setSignupError('Invalid billing address. Please correct any errors below and try submitting your information again.');
                                    break;
                                case 'Cannot save new token, card failed authorization': // cc failed authorization
                                // break omitted
                                case 'Invalid tokenJson': // issues with Flexframe
                                    this.refreshBillingInfo();
                                    gtm(errorPurchaseEvent('Please verify your billing information and try submitting your information again.'));
                                    convertToPaidStore.setSignupError('Please verify your billing information and try submitting your information again.');
                                    break;
                                default:
                                    // no error defined
                                    convertToPaidStore.setSignupError('Something went wrong. Please try again.');
                                    gtm(errorPurchaseEvent('Something went wrong. Please try again.'));

                                    // track error in Sentry
                                    Sentry.captureMessage('convertToPaidError', {
                                        extra: {
                                            errorMessage: 'error text not validated',
                                            error: data
                                        }
                                    });
                                    break;
                            }
                        } else {
                            // no error defined
                            convertToPaidStore.setSignupError('Something went wrong. Please try again.');
                            gtm(errorPurchaseEvent('Something went wrong. Please try again.'));

                            // track error in Sentry
                            Sentry.captureMessage('convertToPaidError', {
                                extra: {
                                    errorMessage: 'no error text',
                                    error: data
                                }
                            });
                        }
                    });
            } else {
                // direct purchase flow
                const requestBody = {
                    NLS: this.props.numberLockSessionStore.NLS,
                    signupObject: signupObj
                };

                // post to sign up
                directPurchaseStore.postSignup(requestBody, (response) => {
                    const { data } = response;
                    // if invalid data value
                    if (!isPlainObject(data)) {
                        throw new Error('invalid data response');
                    }

                    // alert the emailCapture mechanism of the successful email signup before anything else
                    emailCaptureStore.setSubmittedEmail(formsStore.Email);

                    // const redirectUrl = this.determineRedirectUrl(data);

                    // alert marketing if this was a ruby referal
                    if (productTrackingStore.isRubySignup) productTrackingStore.sendIsRubySignup(formsStore.Email);

                    // ToDo :: Old track conversion. Will remove once confirmed by GA after new updates
                    // gtm(createConversionEvent(data));
                    this.setState({ formSuccessSubmission: true });
                    gtm(submitFormEvent('buy-form'));
                    gtm(purchaseEvent(data));
                    gtm(completeBuyFlowEvent(data));

                    // show qualtrics feedback
                    // qualtricsBannerInit();
                    // qualtricsBannerObserver(true, () => this.redirectConfirmation(redirectUrl));

                    // track SCBeacon: successful direct buy signup
                    scBeacon(createPersonalizedPurchaseEvent('2'));
                    directPurchaseStore.setCreatingAccount(false);
                    directPurchaseStore.setAccountCreated(true);
                    history.push(PATH_SIGNUP_SUCCESS);
                }, (err) => {
                    this.signupErrorHandling(err);
                });
            }

            // error handling for flexframe token creation
        }, (err) => {
            this.flexFrameErrorHandling(err);
        });

        return true;
    }

    determineRedirectUrl(signedUpResponse) {
        let url = '';
        if (this.state.lmiUAPLoginEnabled) {
            url = pathProductLogin();
        } else {
            url = signedUpResponse.InitialPasswordResetUrl ? signedUpResponse.InitialPasswordResetUrl : PATH_PRODUCT_LOGIN;
        }

        return url;
    }

    renderSubmitButton() {
        const { creatingAccount } = this.props.convertToPaidStore.isUserConvertingToPaid ? this.props.convertToPaidStore : this.props.directPurchaseStore;

        return (
            <Button
                data-test="order-submit-button"
                disabled={!this.props.convertToPaidStore.canTrialBeConverted || creatingAccount}
                id="submit-button"
                modifier={`${styles['submit-button']}`}
                onClick={() => this.submitOrder()}
            >
                {this.state.createButtonText}
            </Button>
        );
    }

    autoOrderSubmit = reaction(
        () => this.props.convertToPaidStore.isUserConvertingToPaid && this.props.convertToPaidStore.timeoutErrorStep === 'signup',
        () => {
            this.props.convertToPaidStore.setTimeoutErrorStep('');
            this.submitOrder();
        }
    );

    autoFormInteractionDetection = reaction(
        () => this.props.formsStore.IsFormInteracted,
        () => {
            if (this.props.formsStore.IsFormInteracted) {
                gtm(startFormEvent('buy-form'));
            }
        }
    );

    render() {
        const { pendingStore, flexFrameStore } = this.props;
        const { isUserConvertingToPaid } = this.props.convertToPaidStore;
        const { signupError } = isUserConvertingToPaid ? this.props.convertToPaidStore : this.props.directPurchaseStore;
        const { flexFrameError, isCardSupported, differentValidCardTypesDetected } = flexFrameStore;
        const { estimatePaymentError } = isUserConvertingToPaid ? this.props.convertToPaidStore : this.props.billingInfoStore;

        if (pendingStore.selectedNumber && pendingStore.referenceNumber) return <Redirect push={true} to={PATH_PENDING} />;

        const thereIsCardTypeError = !isCardSupported && flexFrameError;
        if (thereIsCardTypeError || differentValidCardTypesDetected) {
            window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        }

        const isCheckoutABTest = false;

        return (
            <div className={styles.billing}>
                {!isUserConvertingToPaid && <Stepper currentStep="billing" />}
                {isUserConvertingToPaid && <div className={styles['back-btn']}><BackButton currentStep={FLOW_STEP_BILLING} /></div>}
                <HeaderGroup>
                    <h1 className={isCheckoutABTest ? styles.header : ''}>{isUserConvertingToPaid ? 'Complete your registration.' : 'Sign up instantly for Grasshopper!'}</h1>
                    <h2 className={isCheckoutABTest ? styles.subheader : styles['page-subheader']}>{isUserConvertingToPaid ? 'And get right back to using Grasshopper to help run your small business!' : '24/7 Live Support. Unlimited Minutes. Upgrade Anytime.'}</h2>
                </HeaderGroup>
                {signupError && <ApiError data-test="sign-up-error-message">{signupError}</ApiError>}
                {flexFrameError && <ApiError data-test="flexframe-error-message">{flexFrameError}</ApiError>}
                {estimatePaymentError && <ApiError data-test="estimate-error-message">{estimatePaymentError}</ApiError>}
                {isCheckoutABTest ?
                    <div className='form-test'>
                        <form>
                            <AccountInfo isCheckoutABTest={isCheckoutABTest} />
                            <BillingAddress isCheckoutABTest={isCheckoutABTest} />
                            <BillingInfo isCheckoutABTest={isCheckoutABTest} />
                        </form>
                        <section className={styles.footer}>
                            <OrderSummary isCheckoutABTest={isCheckoutABTest} />
                            <div className={styles['submit-order']}>
                                {this.renderSubmitButton()}
                                <NewsletterCheckbox
                                    button={this.state.createButtonText}
                                    isCheckoutABTest={isCheckoutABTest}
                                    modifier={styles.disclaimer}
                                />
                                <div className='form-test__disclaimer'>
                                    <h3 className={styles['next-steps']}>What Happens Next?</h3>
                                    <p className={styles['next-steps-instructions']}>1.  We'll walk you through your account set up online (it's easy!), and follow up with you on your new number in a few days to make sure you're all set.</p>
                                    <p className={styles['next-steps-instructions']}>2.  You'll start using Grasshopper to save time and run your business like a pro!</p>
                                </div>
                            </div>
                        </section>
                    </div>
                    :
                    <Fragment>
                        <form>
                            <AccountInfo isCheckoutABTest={isCheckoutABTest} />
                            <BillingInfo isCheckoutABTest={isCheckoutABTest} />
                        </form>
                        <section className={styles.footer}>
                            <OrderSummary />
                            <div className={styles['submit-order']}>
                                {this.renderSubmitButton()}
                                <NewsletterCheckbox button={this.state.createButtonText} modifier={styles.disclaimer} />
                                <h3 className={styles['next-steps']}>What Happens Next?</h3>
                                <p className={styles['next-steps-instructions']}>1.  On your first login, we’ll walk you through your account setup. It’s easy and only take few minutes!</p>
                                <p className={styles['next-steps-instructions']}>2.  You’ll start using Grasshopper to save time and run your business like a pro!</p>
                            </div>
                        </section>
                    </Fragment>
                }
                {/* load flex frame */}
                <Flexframe />
                {/* load device fingerprint */}
                <FingerPrint />
            </div>
        );
    }
}
